/* AppShell — layout wrapper that renders the Sidebar next to the active
   scene. Used as a layout route; the SignInGate stays outside this
   wrapper so it can take the full viewport.

   ≤768px the desktop rail is replaced by a top app bar + an off-canvas
   drawer hosting the full expanded Sidebar (nav, recent threads + the
   All threads link, row menus, sign-out — everything the rail carries on
   desktop).

   The scene outlet (.app-view) keeps the SAME position in the child list
   for both layouts, so crossing the 768px breakpoint (device rotation,
   window snap) reconciles in place instead of remounting the active scene
   — a remount would wipe composer drafts and in-flight agent turns. */

import { Suspense, useCallback, useEffect, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Sidebar } from './Sidebar';
import { MobileTopBar } from './MobileTopBar';
import { SceneErrorBoundary } from './SceneErrorBoundary';
import { useIsMobile } from '@/lib/useIsMobile';
import { WhoamiProvider } from '@/auth/WhoamiContext';
import { ChatHostProvider, ChatHostView } from './ChatHost';

export function AppShell() {
  const isMobile = useIsMobile();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const goHome = useCallback(() => navigate('/home'), [navigate]);

  // Any navigation closes the drawer — covers nav links, thread resumes,
  // and programmatic navigates (New thread passes a fresh resetKey via
  // location.state, so same-path navigations still close it).
  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname, location.state]);

  useEffect(() => {
    if (!drawerOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setDrawerOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [drawerOpen]);

  return (
    <WhoamiProvider>
    <ChatHostProvider>
    <div className={`app-stage ${isMobile ? 'app-stage--mobile' : ''}`}>
      {isMobile ? <MobileTopBar onMenu={() => setDrawerOpen(true)} /> : <Sidebar />}
      <div className="app-view">
        {/* Scenes are lazy route chunks (App.tsx); Suspense + the error
            boundary sit here so only the scene area blanks/faults — the
            sidebar / top bar never unmount. The boundary is OUTSIDE
            Suspense (a rejected lazy import surfaces to the nearest
            boundary above the suspending Suspense) and resets on any
            navigation via location.key. */}
        <SceneErrorBoundary resetKey={location.key} onNavigateHome={goHome}>
          <Suspense fallback={null}>
            <Outlet />
          </Suspense>
        </SceneErrorBoundary>
        {/* The /home chat, and any busy chat kept out of sight while another
            page is on screen (ChatHost.tsx). */}
        <ChatHostView goHome={goHome} />
      </div>
      {isMobile && (
        <>
          <div
            className={`sb-drawer-backdrop ${drawerOpen ? 'open' : ''}`}
            onClick={() => setDrawerOpen(false)}
            aria-hidden
          />
          {/* When closed, visibility: hidden removes the drawer from the
              a11y tree and the tab order — no aria-hidden juggling. */}
          <div
            className={`sb-drawer ${drawerOpen ? 'open' : ''}`}
            role="dialog"
            aria-modal="true"
            aria-label="Navigation"
          >
            <Sidebar variant="drawer" onNavigate={() => setDrawerOpen(false)} />
          </div>
        </>
      )}
      <style>{`
        .app-stage {
          flex: 1; min-height: 0;
          display: flex; position: relative; overflow: hidden;
        }
        .app-stage--mobile { flex-direction: column; }
        .app-view {
          flex: 1; min-width: 0; min-height: 0;
          display: flex; flex-direction: column; overflow: hidden;
        }
        .sb-drawer-backdrop {
          position: fixed; inset: 0; z-index: 118;
          background: rgba(14, 26, 43, 0.45);
          opacity: 0; visibility: hidden;
          transition: opacity .22s ease, visibility 0s linear .22s;
        }
        .sb-drawer-backdrop.open {
          opacity: 1; visibility: visible;
          transition: opacity .22s ease;
        }
        .sb-drawer {
          position: fixed; top: 0; bottom: 0; left: 0; z-index: 119;
          width: min(320px, 86vw);
          display: flex;
          transform: translateX(-102%);
          visibility: hidden;
          transition: transform .24s ease, visibility 0s linear .24s;
          box-shadow: 0 0 32px rgba(0, 0, 0, 0.25);
          padding-top: env(safe-area-inset-top, 0px);
          padding-bottom: env(safe-area-inset-bottom, 0px);
          background: var(--bg-2);
        }
        .sb-drawer.open {
          transform: none;
          visibility: visible;
          transition: transform .24s ease;
        }
        /* The Sidebar's own rule sets width: 240px — in the drawer it
           fills the drawer shell instead. */
        .sb-drawer .sb { width: 100%; }
        @media (prefers-reduced-motion: reduce) {
          .sb-drawer, .sb-drawer-backdrop { transition: none; }
        }
      `}</style>
    </div>
    </ChatHostProvider>
    </WhoamiProvider>
  );
}
